import React, { useState, useEffect } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
  Modal,
  Switch,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import HeaderWithBackAndTitle from '@components/HeaderWithBackAndTitle';
import Text from '@components/CustomText';
import Button from '@components/Button';
import { getTripHistory } from '@services/api';
import colors from '@constants/color';
import FiltersIcon from '@assets/svg/FiltersIcon';
import MultiSlider from '@ptomasroos/react-native-multi-slider';
import { Calendar } from 'react-native-calendars';
import { format } from 'date-fns';
import { ScaledSheet } from '@utils/scale';

interface Transaction {
  id: string;
  title: string;
  timestamp: string;
  timestamp_txt: string;
  subtitle_lines: string[];
  value: number;
  value_txt: string;
  status: string;
  status_txt: string;
  status_color: string;
  show_status_txt: boolean;
  type: string;
}

interface TransactionHistoryData {
  title: string;
  transactions: Transaction[];
  applied_filters: {
    DATE?: {
      key: string;
      value: string;
    };
    SURGE?: {
      key: string;
      value: string;
    };
    WEIGHT?: {
      key: string;
      value: string;
    };
  };
}

const getWeightRangeKey = (weightRange: [number, number]) => {
  return `${weightRange[0]}_${weightRange[1]}`;
}

const getWeightRangeValue = (weightKey: string) => {
  if (!weightKey) return [0, 0];
  const parts = weightKey.split('_').map(Number);
  if (parts.length !== 2 || parts.some(isNaN)) return [0, 0];
  return parts;
}

const MAX_WEIGHT = 80;
const BADGE_SIZE = 30;
const SLIDER_HORIZONTAL_SPACING = 104; // derived from modal margins and padding

/**
 * TransactionItem Component
 * Displays a single transaction item in the list
 */
const TransactionItem: React.FC<{ item: Transaction }> = ({ item }) => {
  const isPositive = item.value >= 0;
  const valueColor = isPositive ? colors.plain_green : colors.plain_red;

  return (
    <View style={styles.transactionCard}>
      <View style={styles.transactionHeader}>
        <View style={styles.transactionTitleContainer}>
          <Text variant="title_16" style={styles.transactionTitle}>
            {item.title}
          </Text>
          <Text style={styles.timestamp}>{item.timestamp_txt}</Text>
        </View>
        <Text style={[styles.value, { color: valueColor }]}>
          {item.value_txt}
        </Text>
      </View>

      {/* Subtitle Lines */}
      {item.subtitle_lines && item.subtitle_lines.length > 0 && (
        <View style={styles.subtitleContainer}>
          {item.subtitle_lines.map((line, index) => (
            <Text key={index} style={styles.timestamp}>
              {line}
            </Text>
          ))}
        </View>
      )}

      {/* Status Badge (if show_status_txt is true) */}
      {item.show_status_txt && (
        <View style={styles.statusContainer}>
          <View
            style={[
              styles.statusBadge,
              {
                backgroundColor:
                  item.status_color === 'yellow'
                    ? colors.veryLightOrange
                    : item.status_color === 'green'
                      ? colors.veryLightGreen
                      : colors.lightRed,
              },
            ]}
          >
            <Text style={styles.statusText}>{item.status_txt}</Text>
          </View>
        </View>
      )}
    </View>
  );
};

/**
 * EarningsTripHistory Component
 * Displays the transaction history with filters
 */
const EarningsTripHistory: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { width: windowWidth } = useWindowDimensions();
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [historyData, setHistoryData] = useState<TransactionHistoryData | null>(
    null,
  );

  // Filter states
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [surgeEnabled, setSurgeEnabled] = useState<boolean>(false);
  const [weightRangeKey, setWeightRangeKey] = useState<string>(`0_${MAX_WEIGHT}`);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showWeightPicker, setShowWeightPicker] = useState<boolean>(false);
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false);

  const sliderLength = Math.max(0, windowWidth - SLIDER_HORIZONTAL_SPACING);
  const getBadgeLeftPosition = (value: number) => {
    const rawLeft = (value / MAX_WEIGHT) * sliderLength - BADGE_SIZE / 2;
    const maxLeft = Math.max(sliderLength - BADGE_SIZE, 0);
    return Math.min(Math.max(rawLeft, 0), maxLeft);
  };

  // Load data on mount
  useEffect(() => {
    //@ts-ignore
    const earningDate = route?.params?.nav_params?.date || date;
    if (earningDate) {
      setSelectedDate(earningDate);
      applyFilters({ DATE: earningDate });
    } else {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Loads transaction history from API
   */
  const loadTransactionHistory = async (filters?: { DATE?: string; SURGE?: string; WEIGHT?: string }) => {
    try {
      setLoading(true);
      const { data } = await getTripHistory(filters);
      setHistoryData(data);
      // Check if filters are applied from API response
      const appliedFilters = data?.applied_filters || {};
      const hasFilters = !!(appliedFilters.SURGE || appliedFilters.WEIGHT);
      setHasActiveFilters(hasFilters);
      if (data?.applied_filters?.DATE) {
        setSelectedDate(data?.applied_filters?.DATE?.key);
      }
      if (data?.applied_filters?.SURGE) {
        setSurgeEnabled(data?.applied_filters?.SURGE?.value === 'YES');
      }
      setWeightRangeKey(data?.applied_filters?.WEIGHT?.key || '0_50');
    } catch (error) {
      console.error('Error loading transaction history:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clears all filters and reloads data
   */
  const clearAllFilters = async () => {
    setSelectedDate('');
    setSurgeEnabled(false);
    setWeightRangeKey('0_50');
    setHasActiveFilters(false);
    await loadTransactionHistory({
      DATE: '',
      SURGE: 'NO',
      WEIGHT: '0_50',
    });
  };

  /**
   * Applies filters and reloads data
   */
  const applyFilters = async (overrides?: { DATE?: string; SURGE?: boolean; WEIGHT?: string }) => {
    const filters: { DATE?: string; SURGE?: string; WEIGHT?: string } = {};

    const dateToUse = overrides?.DATE !== undefined ? overrides.DATE : selectedDate;
    const surgeToUse = overrides?.SURGE !== undefined ? overrides.SURGE : surgeEnabled;
    const weightRangeToUse = overrides?.WEIGHT !== undefined ? overrides.WEIGHT : weightRangeKey;

    if (dateToUse) {
      filters.DATE = dateToUse;
    }
    filters.SURGE = surgeToUse ? 'YES' : 'NO';

    if (weightRangeToUse) {
      filters.WEIGHT = weightRangeToUse;
    }
    await loadTransactionHistory(filters);
  };

  /**
   * Handles filter button click
   */
  const handleFilterClick = () => {
    if (hasActiveFilters) {
      clearAllFilters();
    }
  };

  /**
   * Handles DATE selection
   */
  const handleDateSelect = (day: { dateString: string }) => {
    setSelectedDate(day.dateString);
    setShowDatePicker(false);
    applyFilters({ DATE: day.dateString });
  };

  /**
   * Handles SURGE toggle
   */
  const handleSurgeToggle = (value: boolean) => {
    setSurgeEnabled(value);
    applyFilters({ SURGE: value });
  };

  /**
   * Handles weight range change
   */
  const handleWeightRangeComplete = () => {
    setShowWeightPicker(false);
    applyFilters();
  };

  const handleCloseWeightPicker = () => {
    setShowWeightPicker(false);
  }

  /**
   * Handles pull-to-refresh action
   */
  const onRefresh = async () => {
    setRefreshing(true);
    await applyFilters();
    setRefreshing(false);
  };

  /**
   * Renders a single transaction item
   */
  const renderItem = ({ item }: { item: Transaction }) => (
    <TransactionItem item={item} />
  );

  /**
   * Renders empty state
   */
  const renderEmptyComponent = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyText}>No transactions found</Text>
    </View>
  );


  if (loading) {
    return (
      <View style={styles.container}>
        {/* @ts-ignore - Navigation typing */}
        <HeaderWithBackAndTitle
          headertitle="Trip History"
          navigation={navigation}
          subHeaderTitle=""
          route={route}
          rightIcon={null}
          RightButton={null}
          goingBackHandler={undefined}
          IsRightIconDropDown={undefined}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.black} />
        </View>
      </View>
    );
  }

  const weightRange = getWeightRangeValue(weightRangeKey);

  return (
    <View style={styles.container}>
      {/* @ts-ignore - Navigation typing */}
      <HeaderWithBackAndTitle
        headertitle="Trip History"
        navigation={navigation}
        subHeaderTitle=""
        route={route}
        rightIcon={null}
        RightButton={null}
        goingBackHandler={undefined}
        IsRightIconDropDown={undefined}
      />
      <View style={styles.filtersContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filtersScrollContent}
        >
          {/* Filter Button */}
          <TouchableOpacity
            style={[styles.filterButton, hasActiveFilters && styles.filterButtonActive]}
            onPress={handleFilterClick}
          >
            <FiltersIcon width={16} height={16} />
            <Text style={styles.filterButtonText}>Filter</Text>
          </TouchableOpacity>

          {/* Date Filter */}
          <TouchableOpacity
            style={[styles.filterPill, selectedDate ? styles.filterPillActive : null]}
            onPress={() => setShowDatePicker(true)}
          >
            <Text style={[styles.filterPillText, selectedDate ? styles.filterPillTextActive : undefined]}>
              {selectedDate ? format(new Date(selectedDate), 'dd MMM') : '15 Sep'}
            </Text>
            <Text style={styles.filterPillIcon}>▼</Text>
          </TouchableOpacity>

          {/* Surge Filter */}
          <View style={[styles.filterPill, styles.surgeFilterPill]}>
            <Text style={styles.filterPillText}>Surge</Text>
            <Switch
              value={surgeEnabled}
              onValueChange={handleSurgeToggle}
              trackColor={{ false: colors.lightGray, true: colors.primary }}
              thumbColor={colors.white}
              style={styles.switch}
            />
          </View>

          {/* Weight Filter */}
          <TouchableOpacity
            style={[styles.filterPill]}
            onPress={() => setShowWeightPicker(true)}
          >
            <Text style={[styles.filterPillText]}>
              Weight
            </Text>
          </TouchableOpacity>
        </ScrollView>

        {/* Date Picker Modal */}
        <Modal
          statusBarTranslucent
          navigationBarTranslucent
          visible={showDatePicker}
          transparent
          animationType="fade"
          onRequestClose={() => setShowDatePicker(false)}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setShowDatePicker(false)}
          >
            <View style={styles.datePickerContainer}>
              <Calendar
                onDayPress={(day: { dateString: string }) => {
                  const selectedDateObj = new Date(day.dateString);
                  const today = new Date();
                  today.setHours(23, 59, 59, 999);

                  if (selectedDateObj <= today) {
                    handleDateSelect(day);
                  }
                }}
                markedDates={{
                  [selectedDate]: { selected: true, selectedColor: colors.primary },
                }}
                theme={{
                  backgroundColor: colors.white,
                  calendarBackground: colors.white,
                  textSectionTitleColor: colors.mediumBlack,
                  selectedDayBackgroundColor: colors.primary,
                  selectedDayTextColor: colors.white,
                  todayTextColor: colors.primary,
                  dayTextColor: colors.mediumBlack,
                  textDisabledColor: colors.lightGray,
                  monthTextColor: colors.mediumBlack,
                  textMonthFontWeight: '600',
                  textDayFontSize: 16,
                  textMonthFontSize: 18,
                }}
              />
            </View>
          </TouchableOpacity>
        </Modal>

        {/* Weight Picker Modal */}
        <Modal
          statusBarTranslucent
          navigationBarTranslucent
          visible={showWeightPicker}
          transparent
          animationType="fade"
          onRequestClose={() => setShowWeightPicker(false)}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={handleCloseWeightPicker}
          >
            <TouchableOpacity
              activeOpacity={1}
              style={styles.bottomSheetContainer}
            >
              {/* Handle indicator */}
              {/* <View style={styles.bottomSheetHandle} /> */}

              <Text style={styles.bottomSheetTitle}>Select Weight</Text>

              <View style={styles.bottomSheetContent}>
                {/* Selected values display - positioned above markers */}
                <View style={[styles.sliderContainer, { width: sliderLength }]}>
                  <View style={[styles.selectedValueBadge, {
                    position: 'absolute',
                    top: -32,
                    left: getBadgeLeftPosition(weightRange[0])
                  }]}>
                    <Text style={styles.selectedValueText}>{weightRange[0]}</Text>
                  </View>
                  <View style={[styles.selectedValueBadge, {
                    position: 'absolute',
                    top: -32,
                    left: getBadgeLeftPosition(weightRange[1])
                  }]}>
                    <Text style={styles.selectedValueText}>{weightRange[1]}</Text>
                  </View>

                  <MultiSlider
                    values={[weightRange[0], weightRange[1]]}
                    min={0}
                    max={MAX_WEIGHT}
                    step={1}
                    sliderLength={sliderLength}
                    onValuesChange={(values: number[]) => {
                      setWeightRangeKey(getWeightRangeKey([values[0], values[1]]));
                    }}
                    selectedStyle={{ backgroundColor: colors.primary, height: 4 }}
                    unselectedStyle={{ backgroundColor: colors.lightGray, height: 4 }}
                    markerStyle={{
                      backgroundColor: colors.primary,
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      borderWidth: 3,
                      borderColor: colors.white,
                      shadowColor: colors.black,
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.15,
                      shadowRadius: 4,
                      elevation: 4,
                    }}
                    trackStyle={{ height: 4, borderRadius: 2 }}
                  />
                </View>
                <View style={[styles.bottomSheetLabels, { width: sliderLength }]}>
                  {Array.from({ length: MAX_WEIGHT / 10 + 1 }, (_, index) => (
                    <Text key={index} style={styles.bottomSheetLabel}>{index * 10}</Text>
                  ))}
                </View>
                <View style={{ width: '100%', alignItems: 'flex-end', paddingTop: 24 }}>
                  <Button btnText="Apply" buttonWidth={120} onPress={handleWeightRangeComplete} />
                </View>
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      </View>
      <FlatList
        data={historyData?.transactions || []}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={renderEmptyComponent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

const styles = ScaledSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primaryBg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    paddingVertical: 12,
  },
  listHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  listHeaderText: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.mediumBlack,
  },
  transactionCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    marginHorizontal: 12,
    marginBottom: 12,
    padding: 16,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  transactionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  transactionTitleContainer: {
    flex: 1,
    marginRight: 12,
  },
  transactionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.mediumBlack,
    marginBottom: 4,
  },
  timestamp: {
    fontSize: 12,
    color: colors.lightMediumBlack,
  },
  value: {
    fontSize: 18,
    fontWeight: '700',
  },
  subtitleContainer: {
    marginTop: 4,
  },
  subtitle: {
    fontSize: 14,
    color: colors.mediumBlacklightBlack,
    marginBottom: 2,
    lineHeight: 20,
  },
  statusContainer: {
    marginTop: 8,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.mediumBlack,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
    color: colors.lightMediumBlack,
  },
  filtersContainer: {
    backgroundColor: colors.white,
    paddingBottom: 12,
  },
  filtersScrollContent: {
    paddingHorizontal: 16,
    gap: 8,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderRadius: 18,
    paddingHorizontal: 10,
    paddingVertical: 0,
    borderWidth: 1,
    borderColor: '#CCCCCC',
    gap: 4,
    height: 32,
  },
  filterButtonActive: {
    // borderColor: colors.primary,
  },
  filterButtonText: {
    fontSize: 12,
    color: colors.mediumBlack,
    fontWeight: '500',
    // lineHeight: 12,
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  filterPill: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderRadius: 18,
    paddingHorizontal: 10,
    paddingVertical: 0,
    borderWidth: 1,
    borderColor: '#CCCCCC',
    gap: 4,
    height: 32,
  },
  filterPillActive: {
    backgroundColor: colors.white,
    borderColor: colors.mediumBlack,
  },
  filterPillText: {
    fontSize: 12,
    color: colors.mediumBlack,
    fontWeight: '500',
    // lineHeight: 12,
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  filterPillTextActive: {
    color: colors.mediumBlack,
    fontWeight: '600',
  },
  filterPillIcon: {
    fontSize: 8,
    color: colors.mediumBlack,
    // lineHeight: 8,
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  surgeFilterPill: {
    paddingVertical: 0,
    paddingHorizontal: 10,
    paddingRight: 6,
  },
  switch: {
    transform: [{ scaleX: 0.7 }, { scaleY: 0.7 }],
    marginLeft: -2,
    marginRight: -4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  datePickerContainer: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 16,
    marginHorizontal: 20,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  },
  bottomSheetContainer: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 24,
    marginHorizontal: 20,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  },
  bottomSheetHandle: {
    width: 40,
    height: 4,
    backgroundColor: colors.lightGray,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  bottomSheetTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.mediumBlack,
    marginBottom: 24,
  },
  bottomSheetContent: {
    paddingHorizontal: 8,
  },
  bottomSheetLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingHorizontal: 0,
  },
  bottomSheetLabel: {
    fontSize: 14,
    color: colors.mediumBlack,
    fontWeight: '500',
  },
  sliderContainer: {
    position: 'relative',
    marginTop: 36,
  },
  selectedValueBadge: {
    backgroundColor: colors.primary,
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectedValueText: {
    fontSize: 9,
    color: colors.white,
    fontWeight: '700',
  },
});

export default EarningsTripHistory;
